import { worldPortraitUrl } from '../shared/worldPortrait';
import { refreshAudioDtos } from './audioDtoMigration';
import type { QueryClient } from '@tanstack/react-query';
import {
  getAssetSizes,
  getAllWorldRelationships,
  getCampaigns,
  getCatalogManifest,
  getCatalogPayload,
  getCharacters,
  getSyncChanges,
  getSyncCursor,
  getTableRounds,
  getWorldElements,
  getWorldTreeWithMeta,
  getWorldTrees,
  type SyncChangesResponse,
  type SyncEntityChanges,
  type WorldTreeFull,
  type WorldTreeSummary,
} from '../api/endpoints';
import { hasCachedAsset, networkAssetUrl, registerCachedAsset } from '../api/assets';
import { queryKeys } from '../api/queryKeys';
import { isLegalAcceptanceRequiredError } from '../auth/legalGateSignal';
import { isCapacitorNativeSync } from '../native/bridge';
import type {
  Campaign,
  Character,
  TableRound,
  WorldElement,
  WorldRelationship,
  WorldTree,
  WorldTreeNode,
} from '../api/types';
import {
  type AssetCacheRecord,
  type CatalogVisibleRecord,
  getAllAssetCacheRecords,
  getMetaValue,
  LOCAL_SCHEMA_VERSION,
  markSchemaVersion,
  putAssetCacheRecord,
  setMetaValue,
  type LorathLocalDatabase,
} from './db';
import {
  readCatalogElements,
  readCatalogRelationships,
  readCatalogState,
  writeCatalog,
} from './catalogDb';

export type SyncStatus = 'idle' | 'bootstrapping' | 'live' | 'offline' | 'error';

// 'bundle' = OTA-Phase 0 (Capgo-Bundle-Download VOR dem Daten/Asset-Bootstrap).
// Sie wird NICHT von der SyncEngine erzeugt, sondern vom OtaProvider, der die
// gleiche BootstrapProgress-Form nutzt, damit das Overlay denselben Balken
// rendern kann. Die Engine muss den erweiterten Union-Typ nur akzeptieren —
// die 'data'/'assets'-Logik bleibt unveraendert.
export type BootstrapPhase = 'bundle' | 'data' | 'assets';

export interface BootstrapProgress {
  phase: BootstrapPhase;
  // Gesamt-Fortschritt 0..100, gewichtet: Daten 0..10, Assets 10..100.
  // (Im optionalen Voll-Prefetch sind die Portrait-Bilder der Loewenanteil;
  // ihre Groesse haengt vom Konto ab.) Fuer die 'bundle'-Phase
  // fuettert der OtaProvider 0..100 direkt aus dem Capgo-Download-Fortschritt.
  percentage: number;
  // Daten-Phase (grobe, aber stetige Schritte fuer die Balken-Kontinuitaet).
  dataStep: number;
  dataSteps: number;
  // Asset-Phase.
  assetsDone: number;
  assetsTotal: number;
  bytesDone: number;   // tatsächlich geladene Bytes (Assets)
  // 0 bedeutet: Groessenplanung fehlgeschlagen, daher blendet das Overlay die
  // MB-Zeile aus. Sonst bleibt der Wert bis zur einmaligen Endkorrektur stabil.
  bytesTotal: number;
  // Warum: ETA in Sekunden (gerundet). null solange keine verwertbare Messung.
  etaSeconds: number | null;
  // true, wenn der Bootstrap gerade auf eine Verbindung wartet (offline).
  waitingForNetwork: boolean;
  // Warum: Gegenstueck zu waitingForNetwork — die Daten-Phase ist gescheitert,
  // OBWOHL eine Verbindung besteht (Server-500, blockiertes/kaputtes IndexedDB,
  // Quota). „Warte auf Internetverbindung…" waere dann eine Luege; das Overlay
  // zeigt stattdessen eine ehrliche Fehler-Ansicht mit Ausweg. Optional, weil
  // auch der OtaProvider diese Form fuer die 'bundle'-Phase erzeugt.
  dataError?: boolean;
  // Zahl der aufeinanderfolgenden gescheiterten Daten-Versuche (0 = keiner).
  failedAttempts?: number;
}

export interface SyncEngineSnapshot {
  status: SyncStatus;
  lastSyncAt: string | null;
  // Warum: nur waehrend 'bootstrapping' befuellt. Beim Status-Wechsel
  // (live / error / offline) verschwindet das Overlay, daher muss das
  // Feld nicht explizit geleert werden.
  bootstrapProgress?: BootstrapProgress;
}

export interface SyncEngineOptions {
  db: LorathLocalDatabase;
  queryClient: QueryClient;
  intervalMs?: number;
  // Nur explizite Offline-Laeufe duerfen alle Bilder vorladen. Standardmaessig
  // kommen Bilder bei Anzeige ueber assetUrl() aus Cache oder Netz; auch nativ
  // startet kein grosser Voll-Download auf Mobilfunk.
  prefetchAssets?: boolean;
}

type SyncListener = () => void;
type ServerEntityType =
  | 'campaign'
  | 'character'
  | 'world_element'
  | 'world_relationship'
  | 'world_tree'
  | 'world_tree_node'
  | 'table_round';

interface BootstrapData {
  cursor: number;
  campaigns: Campaign[];
  characters: Character[];
  worldElements: WorldElement[];
  worldRelationships: WorldRelationship[];
  worldTrees: WorldTree[];
  worldTreeNodes: WorldTreeNode[];
  tableRounds: TableRound[];
}

interface CapacitorSubscription {
  remove?: () => Promise<void> | void;
}

interface CapacitorAppPlugin {
  addListener?: (
    eventName: 'appStateChange',
    handler: (state: { isActive?: boolean }) => void,
  ) => Promise<CapacitorSubscription> | CapacitorSubscription;
}

interface CapacitorWindow extends Window {
  Capacitor?: {
    Plugins?: {
      App?: CapacitorAppPlugin;
    };
  };
}

const DEFAULT_INTERVAL_MS = 20_000;
const PAGE_LIMIT = 500;
const INITIAL_RETRY_MS = 5_000;
const MAX_RETRY_MS = 300_000;
const BOOTSTRAP_CONCURRENCY = 6;
const RATE_LIMIT_FALLBACK_MS = 10_000;
// Warum: gegen endlose 429-Schleifen, die den Bootstrap ewig laufen lassen
// (Android-Hänger "Deine Inhalte werden eingerichtet"). Wir geben dem Server
// großzügig Zeit, uns aber nicht für immer.
const MAX_RATE_LIMIT_TOTAL_MS = 90_000;
const MAX_RATE_LIMIT_ATTEMPTS = 6;
// Warum: harte Obergrenze NUR fuer die DATEN-Phase (Fetch + Dexie-Write).
// Mit der Bulk-Beziehungs-Route ist die Datenphase in Sekunden fertig; 120s
// ist eine grosszuegige Reserve gegen einen haengenden einzelnen Request.
// Die ASSET-Phase (Bilder) faellt NICHT unter dieses Timeout — sie darf
// Minuten dauern und hat ihren eigenen Per-Asset-Timeout.
const BOOTSTRAP_DATA_TIMEOUT_MS = 120_000;
// Warum: deterministische Schrittzahl der Daten-Phase fuer den Balken.
// 1 = Cursor geladen, 2 = Basis-Fetches (Kampagnen/Charaktere/Welten/…),
// 3 = Details (Bulk-Beziehungen + Tree-Details), 4 = Dexie-Write.
const DATA_STEPS = 4;
// Warum: maximale Wartezeit auf den Multi-Tab-Bootstrap-Lock. Laeuft sie ab
// (haengender/eingefrorener Halter-Tab), bootstrappt dieser Tab OHNE Lock —
// ein doppelter Bootstrap ist unangenehm, ein Deadlock waere fatal.
const BOOTSTRAP_LOCK_WAIT_MS = 30_000;
// Asset-Phase (Portrait-Bilder offline-faehig in den Dexie-Blob-Cache laden).
const ASSET_CONCURRENCY = 4;
const ASSET_FETCH_TIMEOUT_MS = 30_000;
// Pauschale ausschliesslich fuer erlaubte, serverseitig nicht gefundene Pfade.
// Bei einem Fehler des Groessen-Requests gibt es bewusst gar keine MB-Anzeige.
const AVG_ASSET_BYTES = 62 * 1024;

type ProgressStep = (completed: number) => void;

function nowIso(): string {
  return new Date().toISOString();
}

function isBrowserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

function getWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

// Web Locks gibt es nur in sicheren Kontexten und nicht in jsdom/aelteren
// Browsern — fehlt die API, laeuft alles wie bisher ohne Lock.
function getLockManager(): LockManager | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as Partial<Navigator>).locks;
  return typeof locks?.request === 'function' ? locks : null;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function retryDelayFromError(error: unknown): number | null {
  const candidate = error as {
    status?: unknown;
    retryAfterMs?: unknown;
    data?: unknown;
  };
  if (candidate.status !== 429) return null;
  if (typeof candidate.retryAfterMs === 'number' && Number.isFinite(candidate.retryAfterMs)) {
    return Math.max(1_000, candidate.retryAfterMs);
  }
  const data = candidate.data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const waitSeconds = record.waitSeconds ?? record.retryAfterSeconds;
    if (typeof waitSeconds === 'number' && Number.isFinite(waitSeconds)) {
      return Math.max(1_000, waitSeconds * 1000);
    }
    const retryAfterMs = record.retryAfterMs;
    if (typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs)) {
      return Math.max(1_000, retryAfterMs);
    }
  }
  return RATE_LIMIT_FALLBACK_MS;
}

async function withRateLimitRetry<T>(task: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  let attempt = 0;
  for (;;) {
    attempt += 1;
    try {
      return await task();
    } catch (error) {
      const retryDelay = retryDelayFromError(error);
      if (retryDelay === null) throw error;

      // Warum: endlose 429-Loops dürfen den Bootstrap nicht dauerhaft
      // festhalten. Wir geben nach spätestens MAX_RATE_LIMIT_ATTEMPTS oder
      // MAX_RATE_LIMIT_TOTAL_MS auf und werfen den letzten Fehler weiter,
      // damit der Caller sauber auf 'error' umschalten kann.
      const elapsed = Date.now() - startedAt;
      if (
        attempt >= MAX_RATE_LIMIT_ATTEMPTS
        || elapsed + Math.min(retryDelay, MAX_RETRY_MS) > MAX_RATE_LIMIT_TOTAL_MS
      ) {
        throw error;
      }

      await wait(Math.min(retryDelay, MAX_RETRY_MS));
    }
  }
}

class BootstrapTimeoutError extends Error {
  constructor(public readonly code: 'bootstrap_timeout') {
    super('bootstrap_timeout');
    this.name = 'BootstrapTimeoutError';
  }
}

// Warum: harte Obergrenze für den Bootstrap — unabhängig davon, ob der
// einzelne Request hängt, mehrere Retries laufen oder die Dexie-Transaktion
// klemmt. Der Status muss irgendwann den "bootstrapping"-Zustand verlassen.
function withOverallTimeout<T>(promise: Promise<T>, timeoutMs: number, code: 'bootstrap_timeout'): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new BootstrapTimeoutError(code)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== null) clearTimeout(timeoutId);
  });
}

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  task: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = [];
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    results.push(...await Promise.all(batch.map(task)));
  }
  return results;
}

async function settleRecord<T extends Record<string, Promise<unknown>>>(
  input: T,
): Promise<{ [K in keyof T]: Awaited<T[K]> }> {
  const entries = Object.entries(input) as [keyof T, Promise<unknown>][];
  const settled = await Promise.allSettled(entries.map(([, promise]) => promise));
  const failed = settled.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failed) throw failed.reason instanceof Error ? failed.reason : new Error('sync_fetch_failed');

  return Object.fromEntries(settled.map((result, index) => {
    const key = entries[index]?.[0];
    if (!key || result.status !== 'fulfilled') throw new Error('sync_fetch_failed');
    return [key, result.value];
  })) as { [K in keyof T]: Awaited<T[K]> };
}

function toWorldTreeRecord(fullTree: WorldTreeFull): WorldTree {
  const tree: Partial<WorldTreeFull> = { ...fullTree };
  delete tree.nodes;
  delete tree.elements;
  delete tree.relationships;
  delete tree.lineSettings;
  return tree as WorldTree;
}

function dedupeById<T extends { id: number }>(items: T[]): T[] {
  const byId = new Map<number, T>();
  for (const item of items) byId.set(item.id, item);
  return [...byId.values()];
}

// Sammelt alle Portrait-Pfade aus den Bootstrap-Daten (Welt-Elemente +
// Charaktere + Kampagnen-Cover). Die beiden Präfixe spiegeln exakt die
// serverseitige Asset-Allowlist; fremde relative/API-Pfade werden nie geladen.
function collectAssetPaths(data: {
  worldElements: WorldElement[];
  characters: Character[];
  campaigns: Campaign[];
}): string[] {
  const set = new Set<string>();
  const add = (raw: unknown, barePrefix?: '/uploads/portraits/'): void => {
    if (typeof raw !== 'string') return;
    const value = raw.trim();
    if (!value) return;
    if (/^(https?:\/\/|data:|blob:)/i.test(value)) return;
    const normalized = value.startsWith('/') ? value : `/${value}`;
    if (normalized.startsWith('/img/') || normalized.startsWith('/uploads/portraits/')) {
      set.add(normalized);
      return;
    }
    // Planner-Cover werden als blosser Dateiname gespeichert. Genau diese
    // Form loest auch CampaignCard unter /uploads/portraits/ auf.
    if (barePrefix && !value.includes('/')) set.add(`${barePrefix}${encodeURIComponent(value)}`);
  };
  for (const element of data.worldElements) {
    add(worldPortraitUrl(element));
    add((element as { portraitUrl?: unknown }).portraitUrl);
    add((element as { portrait?: unknown }).portrait);
  }
  for (const character of data.characters) {
    add((character as { portraitUrl?: unknown }).portraitUrl);
    add((character as { portrait?: unknown }).portrait);
  }
  // Phase 2 (Planner): Kampagnen-Cover-Bild (campaigns.cover_portrait) muss
  // genauso offline verfuegbar sein wie Charakter/Welt-Portraits, damit die
  // Dashboard-Karten ohne Netz ein Bild zeigen koennen. Der Server speichert
  // hier meist nur den Dateinamen; CampaignCard setzt dasselbe Upload-Praefix.
  for (const campaign of data.campaigns) add(campaign.cover_portrait, '/uploads/portraits/');
  return [...set];
}

// Lädt EIN Asset als Blob mit Abbruch-Timeout. Wirft bei Timeout/!ok — der
// Aufrufer entscheidet über Retry/Skip (ein Asset-Fehler darf den Bootstrap
// NIE blockieren).
async function fetchBlobWithTimeout(url: string, timeoutMs: number): Promise<Blob> {
  if (typeof fetch !== 'function') throw new Error('fetch_unavailable');
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? globalThis.setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!res.ok) throw new Error(`asset_http_${res.status}`);
    return await res.blob();
  } finally {
    if (timer !== null) globalThis.clearTimeout(timer);
  }
}

async function fetchTreeDetails(
  summaries: WorldTreeSummary[],
): Promise<{ worldTrees: WorldTree[]; worldTreeNodes: WorldTreeNode[]; treeRelationships: WorldRelationship[] }> {
  if (!summaries.length) return { worldTrees: [], worldTreeNodes: [], treeRelationships: [] };

  const fullTrees = await mapInBatches(
    summaries,
    BOOTSTRAP_CONCURRENCY,
    (tree) => withRateLimitRetry(async () => (await getWorldTreeWithMeta(tree.id)).data),
  );
  return {
    worldTrees: fullTrees.map(toWorldTreeRecord),
    worldTreeNodes: dedupeById(fullTrees.flatMap((tree) => tree.nodes ?? [])),
    treeRelationships: dedupeById(fullTrees.flatMap((tree) => tree.relationships ?? [])),
  };
}

async function fetchBootstrapData(onStep?: ProgressStep): Promise<BootstrapData> {
  const { cursor } = await getSyncCursor();
  onStep?.(1);

  const base = await settleRecord({
    campaigns: getCampaigns(),
    characters: getCharacters(),
    // Warum: all_kosmos=1 zieht ALLE Kosmen des Users in den Offline-Store.
    // Der aktive Kosmos wird danach CLIENT-seitig gefiltert (applyWorldElementFilter
    // + useKosmos), damit der Kosmos-Wechsel OFFLINE ohne erneuten Sync
    // funktioniert (Produkt-Anforderung: "Offline muss tadellos funktionieren").
    // Der GET-Route-Pfad ist nur über das ?all_kosmos=true-Flag unlocked —
    // andere Aufrufer (Charakter-Erstellung etc.) bleiben auf aktiven Kosmos
    // gescoped.
    worldElements: getWorldElements({ all_kosmos: '1' }),
    tableRounds: getTableRounds(),
    worldTreeSummaries: getWorldTrees(),
  });
  onStep?.(2);

  const details = await settleRecord({
    // Bulk statt N+1: EIN Request für ALLE Beziehungen des Users (vorher pro
    // Quelle einzeln → mit den Beispiel-Kosmos 321+ Requests → Erststart-Hänger).
    relationships: withRateLimitRetry(() => getAllWorldRelationships()),
    treeDetails: fetchTreeDetails(base.worldTreeSummaries),
  });
  onStep?.(3);

  return {
    cursor,
    campaigns: base.campaigns,
    characters: base.characters,
    worldElements: base.worldElements,
    worldRelationships: dedupeById([
      ...details.relationships,
      ...details.treeDetails.treeRelationships,
    ]),
    worldTrees: details.treeDetails.worldTrees,
    worldTreeNodes: details.treeDetails.worldTreeNodes,
    tableRounds: base.tableRounds,
  };
}

function hasChanges<T>(changes: SyncEntityChanges<T>): boolean {
  return changes.upserts.length > 0 || changes.deletions.length > 0;
}

export class SyncEngine {
  private readonly db: LorathLocalDatabase;
  private readonly queryClient: QueryClient;
  private readonly intervalMs: number;
  // Warum: siehe SyncEngineOptions.prefetchAssets — steuert die Asset-Phase
  // UND das Rehydrieren des Blob-Caches (beides ergibt nur nativ Sinn).
  private readonly prefetchAssetsEnabled: boolean;
  // Warum: db.name traegt die User-ID (lorath-local-u<id>), der Lock ist damit
  // automatisch pro Account eindeutig.
  private readonly bootstrapLockName: string;
  private readonly listeners = new Set<SyncListener>();
  private snapshot: SyncEngineSnapshot = { status: 'idle', lastSyncAt: null };
  private retryDelayMs = INITIAL_RETRY_MS;
  private intervalId: number | null = null;
  private retryId: number | null = null;
  private idleId: number | null = null;
  private started = false;
  private pollPromise: Promise<void> | null = null;
  private bootstrapPromise: Promise<void> | null = null;
  private removeNativeForegroundListener: (() => void) | null = null;
  // Warum: Startzeit des aktuellen Bootstrap-Laufs, damit der Fortschritts-
  // balken eine monotone ETA ableiten kann. Wird in bootstrap() gesetzt und
  // nach Abschluss (oder Fehler) auf null zurueckgesetzt, damit ein Folge-
  // Bootstrap sauber neu misst.
  private bootstrapProgressStartAt: number | null = null;
  // Warum: getrennte Startzeit der Asset-Phase fuer eine Byte-basierte ETA.
  private assetPhaseStartAt: number | null = null;
  // Nach der Cache-Inventur einmal geplanter Gesamtwert. null blendet bei
  // fehlgeschlagenem Groessen-Request die MB-Zeile fuer den ganzen Lauf aus.
  private assetBytesTotalPlanned: number | null = null;
  // Gecachte Bytes gehoeren zum Fortschritt, aber nicht zur gemessenen
  // Downloadrate dieses Laufs und duerfen die ETA daher nicht beschleunigen.
  private assetBytesAtDownloadStart = 0;
  // Pro Asset-Lauf darf bytesTotal hoechstens einmal ganz am Ende auf die
  // tatsaechlich geladenen Bytes korrigiert werden (hoch oder runter).
  private assetTotalCorrected = false;
  // Warum: Guard gegen parallele Asset-Resume-Laeufe (analog bootstrapPromise).
  private assetResumePromise: Promise<void> | null = null;
  // Warum: Asset-Cache nur einmal pro Engine-Lauf aus Dexie rehydrieren.
  private assetCacheRehydrated = false;
  // Warum: In-Memory-Quelle der Wahrheit, dass die Asset-Phase in DIESER Session
  // vollständig durchlief — IMMUN gegen ein fehlgeschlagenes setMetaValue('assetsDone').
  // Verhindert, dass das Gate innerhalb einer Session erneut auftaucht (Review:
  // „ewig hängendes Gate" bei Dexie-Schreibfehlern).
  private assetsCompleted = false;
  // Warum: Zaehler der in Folge gescheiterten Daten-Versuche. Ab der dritten
  // erfolglosen Runde zeigt das Overlay statt des Balkens eine Fehler-Ansicht.
  private bootstrapFailures = 0;
  // W5-E: Beim naechsten Delta die VOLLSTAENDIGE Katalog-Verweisliste anfordern
  // (nach dem erstmaligen Laden/Erneuern der Katalog-Nutzlast).
  private catalogFullRequested = false;
  // W5-E: Der Server hat eine andere Katalogversion gemeldet — Nutzlast nach
  // dem Seitendurchlauf einmal nachladen.
  private catalogReloadPending = false;
  // Warum: Ein Startup-Gate (Re-Akzept der Rechtstexte) sperrt die Nutzung —
  // der Server weist dann fast jede /api-Route mit 409 ab. Solange gesetzt,
  // laufen weder Bootstrap noch Poll; das Einrichtungs-Vollbild bleibt aus,
  // damit der Zustimmungsdialog sichtbar ist. Gesetzt vom SyncProvider.
  private usageBlocked = false;

  constructor(options: SyncEngineOptions) {
    this.db = options.db;
    this.queryClient = options.queryClient;
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.prefetchAssetsEnabled = options.prefetchAssets ?? false;
    this.bootstrapLockName = `lorath-bootstrap-${options.db.name}`;
  }

  getSnapshot = (): SyncEngineSnapshot => this.snapshot;

  subscribe = (listener: SyncListener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  start(): void {
    if (this.started) return;
    this.started = true;
    // Vorab gecachte Portrait-Blobs sofort als Object-URL registrieren, damit
    // Bilder auch OHNE (Re-)Bootstrap offline sichtbar sind — bei jedem Start.
    // Nur nativ: im Browser gibt es keinen Offline-Fall, und assetUrl() faellt
    // ohne Registrierung sauber auf die Netz-URL zurueck (api/assets.ts) — ein
    // evtl. noch gefuellter Blob-Cache bleibt also folgenlos liegen, statt bei
    // jedem Boot Speicher + IO fuer hunderte Object-URLs zu kosten.
    if (isCapacitorNativeSync()) void this.rehydrateAssetCache();
    this.attachBrowserListeners();
    this.attachNativeForegroundListener();
    this.restartInterval();

    this.scheduleIdleTask(() => {
      void this.ensureBootstrappedThenPoll();
    });
  }

  stop(): void {
    this.started = false;
    this.clearInterval();
    this.clearRetry();
    this.clearIdleTask();
    this.detachBrowserListeners();
    this.removeNativeForegroundListener?.();
    this.removeNativeForegroundListener = null;
    this.setSnapshot({ status: 'idle', lastSyncAt: this.snapshot.lastSyncAt });
  }

  /**
   * Startup-Gate sperrt/entsperrt die Nutzung (legalGateSignal.ts). Sperren
   * blendet ein laufendes Einrichtungs-Vollbild aus; Entsperren startet sofort
   * einen frischen Anlauf (ohne Backoff und Fehlerzaehler der 409er-Runde).
   */
  setUsageBlocked(blocked: boolean): void {
    if (this.usageBlocked === blocked) return;
    this.usageBlocked = blocked;
    if (blocked) {
      this.clearRetry();
      if (this.snapshot.status === 'bootstrapping' || this.snapshot.status === 'error') {
        this.setSnapshot({ status: 'idle', lastSyncAt: this.snapshot.lastSyncAt });
      }
      return;
    }
    this.retryDelayMs = INITIAL_RETRY_MS;
    this.bootstrapFailures = 0;
    if (!this.started) return;
    this.clearRetry();
    void this.ensureBootstrappedThenPoll();
  }

  async forceSync(): Promise<void> {
    await this.ensureBootstrappedThenPoll({ allowStopped: true });
  }

  async bootstrap(): Promise<void> {
    if (this.bootstrapPromise) return this.bootstrapPromise;
    if (this.usageBlocked) return;

    this.bootstrapPromise = (async () => {
      try {
        this.bootstrapProgressStartAt = Date.now();

        if (!isBrowserOnline()) {
          // Erststart offline: das Gate sichtbar lassen + warten. KEIN
          // „trotzdem fortfahren" — ohne Daten schlägt in der App alles fehl.
          // Der Retry holt es nach, sobald die Verbindung steht.
          this.reportDataProgress(0, true);
          this.scheduleRetry();
          return;
        }

        this.reportDataProgress(0);
        // Multi-Tab: zwei Tabs desselben Users wuerden sonst BEIDE bootstrappen
        // und mit clear()+bulkPut gegeneinander schreiben. Der wartende Tab
        // erkennt nach Erhalt des Locks den fremden Erfolg und ueberspringt.
        const skipped = await this.withBootstrapLock(async () => {
          const doneMeanwhile = await getMetaValue(this.db, 'bootstrapDone', false).catch(() => false);
          if (doneMeanwhile) return true;

          // Harte Obergrenze NUR um die Daten-Phase (Fetch + Dexie-Write).
          const data = await withOverallTimeout(
            fetchBootstrapData((step) => this.reportDataProgress(step)),
            BOOTSTRAP_DATA_TIMEOUT_MS,
            'bootstrap_timeout',
          );
          // W5-E: Der geteilte Katalog wird GETRENNT geholt — einmal pro
          // Geraet, kontouebergreifend, versioniert. Schlaegt das fehl (alter
          // Server-Slot), bleibt der klassische Weg unveraendert bestehen.
          const catalogVersion = await this.ensureCatalogLoaded();
          const catalogSets = catalogVersion === null ? null : await SyncEngine.catalogIdSets();
          // Daten zuerst schreiben (setzt bootstrapDone=true) — die App ist danach
          // nutzbar, selbst wenn sie waehrend der Bilder-Phase beendet wird.
          await this.writeBootstrapData(
            data,
            catalogVersion !== null && catalogSets && catalogSets.elements.size > 0
              ? {
                version: catalogVersion,
                elementIds: catalogSets.elements,
                relationshipIds: catalogSets.relationships,
              }
              : null,
          );
          this.reportDataProgress(DATA_STEPS); // 10 %
          // Optionaler Offline-Voll-Prefetch (10 → 100 %), nur auf ausdruecklichen
          // Aufruf. Standardmaessig gibt es diese Phase auch nativ NICHT:
          // der Leerfall-Pfad markiert sie sofort als erledigt, das Gate endet
          // nach der Daten-Phase, Bilder kommen ueber das Netz (assetUrl faellt
          // auf networkAssetUrl zurueck; /uploads/portraits ist 30d immutable).
          await this.prefetchAssets(this.prefetchAssetsEnabled ? collectAssetPaths(data) : []);
          return false;
        });

        this.bootstrapFailures = 0;
        this.retryDelayMs = INITIAL_RETRY_MS;
        // Uebersprungener Lauf (anderer Tab war schneller): den Status NICHT
        // selbst auf 'live' setzen — sonst verschwaende das Gate, waehrend der
        // andere Tab noch in seiner Asset-Phase steckt. Das folgende poll()
        // (ensureBootstrappedThenPoll) entscheidet anhand der Meta-Flags.
        if (skipped) return;
        this.setSnapshot({ status: 'live', lastSyncAt: nowIso() });
        this.invalidateAllSyncQueries();
      } catch (error) {
        // Wenn die DATEN schon geschrieben sind, warf nur die Asset-Phase —
        // die App ist nutzbar; Bilder werden bei Bedarf spaeter nachgeladen.
        const dataDone = await getMetaValue(this.db, 'bootstrapDone', false).catch(() => false);
        if (dataDone) {
          this.assetsCompleted = true;
          await setMetaValue(this.db, 'assetsDone', true).catch(() => undefined);
          this.bootstrapFailures = 0;
          this.retryDelayMs = INITIAL_RETRY_MS;
          this.setSnapshot({ status: 'live', lastSyncAt: nowIso() });
          this.invalidateAllSyncQueries();
        } else if (isLegalAcceptanceRequiredError(error)) {
          // Zustimmung zu neuen Rechtstexten fehlt (Server-Gate, 409): kein
          // Server-/Netzfehler. Kein Fehlerzaehler, keine Fehler-Ansicht, kein
          // Vollbild — es verdeckte sonst den Zustimmungsdialog. Das Gate holt
          // seinen Status ueber das 409-Ereignis (api/client.ts) neu; der Retry
          // bleibt als Selbstheilung, laeuft aber leer, solange gesperrt.
          await setMetaValue(this.db, 'bootstrapDone', false).catch(() => undefined);
          this.setSnapshot({ status: 'idle', lastSyncAt: this.snapshot.lastSyncAt });
          this.scheduleRetry();
        } else {
          // Daten-Phase fehlgeschlagen: das Gate NICHT verlassen (sonst landet
          // der User in einer leeren App) — sichtbar „warten" + Retry.
          await setMetaValue(this.db, 'bootstrapDone', false).catch(() => undefined);
          this.bootstrapFailures += 1;
          // Echtes Offline (der Retry holt es von allein nach) vs. Server-/
          // Speicher-Fehler (500, blockiertes IndexedDB, Quota): nur Letzteres
          // darf im Overlay eine Fehler-Ansicht mit Ausweg zeigen — „Warte auf
          // Internetverbindung…" waere hier schlicht falsch.
          const offline = !isBrowserOnline();
          this.reportDataProgress(0, offline, !offline);
          this.scheduleRetry();
        }
      } finally {
        this.bootstrapPromise = null;
        this.bootstrapProgressStartAt = null;
        this.assetPhaseStartAt = null;
      }
    })();

    return this.bootstrapPromise;
  }

  // Serialisiert den Bootstrap ueber alle Tabs desselben Users (Web Locks).
  // Fehlt die API (jsdom, aeltere Browser, unsicherer Kontext) oder wird der
  // Lock nicht binnen BOOTSTRAP_LOCK_WAIT_MS erteilt, laeuft der Bootstrap wie
  // bisher OHNE Lock — ein Deadlock hier wuerde die App fuer ALLE sperren.
  private async withBootstrapLock<T>(run: () => Promise<T>): Promise<T> {
    const locks = getLockManager();
    if (!locks) return run();

    // Warum: Fehler AUS dem Lauf muessen unveraendert nach oben — nur ein NICHT
    // erteilter Lock darf auf den lockfreien Weg zurueckfallen (sonst liefe der
    // Bootstrap nach einem echten Fehler ein zweites Mal).
    let entered = false;
    const guarded = async (): Promise<T> => {
      entered = true;
      return run();
    };

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller
      ? globalThis.setTimeout(() => controller.abort(), BOOTSTRAP_LOCK_WAIT_MS)
      : null;
    try {
      return controller
        ? await locks.request(this.bootstrapLockName, { signal: controller.signal }, guarded)
        : await locks.request(this.bootstrapLockName, guarded);
    } catch (error) {
      if (entered) throw error;
      console.warn('[Sync] Bootstrap-Lock nicht erhalten — Bootstrap laeuft ohne Lock.', error);
      return run();
    } finally {
      if (timer !== null) globalThis.clearTimeout(timer);
    }
  }

  async poll(): Promise<void> {
    if (this.pollPromise) return this.pollPromise;
    this.pollPromise = this.runPoll().finally(() => {
      this.pollPromise = null;
    });
    return this.pollPromise;
  }

  private async runPoll(): Promise<void> {
    if (this.usageBlocked) return;
    if (!isBrowserOnline()) {
      this.setSnapshot({ status: 'offline', lastSyncAt: this.snapshot.lastSyncAt });
      return;
    }

    try {
      await refreshAudioDtos(this.db, getCharacters, () => getWorldElements({ all_kosmos: '1' }));
      let cursor = await getMetaValue<number>(this.db, 'syncCursor', 0);
      let hasMore = true;
      // W5-E: Nur mit lokalem Katalog melden wir uns an. Ohne (alter Server,
      // fehlgeschlagener Erstabruf) bleibt die Anfrage byte-gleich zu frueher
      // und der Server liefert die Katalogzeilen wie bisher aus.
      let catalogVersion = await getMetaValue<number>(this.db, 'catalogVersion', 0).catch(() => 0);
      if (catalogVersion > 0) {
        // Selbstheilung: Der Merker sagt „Katalog liegt vor", die Katalog-DB
        // ist aber weg oder veraltet (Speicher geraeumt, abgebrochener
        // Schreibvorgang). Ohne diese Probe blieben die Verweise ohne Inhalte
        // stehen — die Welt saehe halb leer aus, und weil Version == Version
        // ist, meldete der Server auch nie `stale`.
        const state = await readCatalogState();
        if (!state || state.version !== catalogVersion) {
          const reloaded = await this.ensureCatalogLoaded();
          if (reloaded !== null) {
            await setMetaValue(this.db, 'catalogVersion', reloaded).catch(() => undefined);
            catalogVersion = reloaded;
          } else {
            // Nicht erreichbar: ohne ?catalog= weiterlaufen, dann liefert der
            // Server die Katalogzeilen wieder auf dem klassischen Weg.
            catalogVersion = 0;
          }
        }
      }

      while (hasMore) {
        // Ohne Katalog bleibt der Aufruf exakt der alte (kein dritter
        // Parameter, keine zusaetzlichen Query-Felder).
        const page = catalogVersion > 0
          ? await getSyncChanges(cursor, PAGE_LIMIT, {
            version: catalogVersion,
            full: this.catalogFullRequested,
          })
          : await getSyncChanges(cursor, PAGE_LIMIT);
        // Die volle Verweisliste ist mit DIESER Antwort abgeholt.
        if (catalogVersion > 0 && page.catalog) this.catalogFullRequested = false;
        const nextCursor = await this.applyChangePage(page);
        hasMore = page.hasMore && nextCursor > cursor;
        if (nextCursor <= cursor) break;
        cursor = nextCursor;
      }

      // Der Server faehrt eine andere Katalogversion (Neubau/Deploy): Nutzlast
      // einmal nachladen. Die Verweisliste kam mit der stale-Antwort bereits
      // vollstaendig fuer die NEUE Version — nach dem Nachladen passen Inhalte
      // und Verweise wieder zusammen.
      if (this.catalogReloadPending) {
        this.catalogReloadPending = false;
        const reloaded = await this.ensureCatalogLoaded();
        if (reloaded !== null) {
          await setMetaValue(this.db, 'catalogVersion', reloaded).catch(() => undefined);
          this.invalidateTouchedQueries(new Set<ServerEntityType>(['world_element', 'world_relationship']));
        }
      }

      this.retryDelayMs = INITIAL_RETRY_MS;
      // Erst 'live', wenn Daten UND Bilder geladen sind — sonst würde das Gate
      // während der (minutenlangen) Asset-Phase fälschlich verschwinden.
      const bootstrapDone = await getMetaValue(this.db, 'bootstrapDone', false).catch(() => false);
      const assetsDone = await getMetaValue(this.db, 'assetsDone', false).catch(() => false);
      // assetsCompleted (In-Memory) deckt den Fall ab, dass die Asset-Phase lief,
      // aber das setMetaValue('assetsDone') fehlschlug — sonst bliebe das Gate kleben.
      if (bootstrapDone && (assetsDone || this.assetsCompleted)) {
        this.setSnapshot({ status: 'live', lastSyncAt: nowIso() });
      } else {
        this.setSnapshot({ status: 'bootstrapping', lastSyncAt: this.snapshot.lastSyncAt });
      }
    } catch (error) {
      // 409 legal_acceptance_required ist kein Sync-Fehler (kein SyncErrorBanner,
      // Intervall laeuft weiter); bootstrapDone bleibt, die Daten sind lokal da.
      const legalBlocked = isLegalAcceptanceRequiredError(error);
      const status: SyncStatus = legalBlocked
        ? (this.snapshot.status === 'live' ? 'live' : 'idle')
        : isBrowserOnline() ? 'error' : 'offline';
      this.setSnapshot({ status, lastSyncAt: this.snapshot.lastSyncAt });
      this.scheduleRetry();
    }
  }

  private async ensureBootstrappedThenPoll(options: { allowStopped?: boolean } = {}): Promise<void> {
    if (!options.allowStopped && !this.started && this.snapshot.status === 'idle') return;
    if (this.usageBlocked) return;

    // Warum: Schema-Bump-Heilung. Beim Upgrade von LOCAL_SCHEMA_VERSION=1 auf 2
    // (kosmos_id-Index) liegen in der lokalen Dexie bereits worldElements-Rows,
    // die noch kein kosmos_id tragen — der Server liefert es erst beim nächsten
    // Re-Bootstrap.
    //
    // WICHTIG: Die Heilung darf NICHT offline greifen. Startet ein aktualisierter
    // User die App zuerst OFFLINE, würde `bootstrap()` ohne Schreiben zurück-
    // kehren → bootstrapDone bleibt false → useLocalFirstList wirft
    // SyncNotBootstrappedError → ALLE Offline-Listen (campaigns/characters/
    // world_elements/…) wären unlesbar. Wir setzen bootstrapDone daher nur dann
    // zurück, wenn wir gleich online heilen können. Offline bleibt der alte
    // v1-Snapshot lesbar; die Rows ohne kosmos_id werden vom clientseitigen
    // Filter (applyWorldElementFilter: kosmos_id == null) wie systemüber-
    // greifende Seeds behandelt und sind in allen Kosmen sichtbar — das ist
    // der bewusste INHALT-ZEIGENDE Übergang (besser als leerer Screen). Beim
    // ersten Online-Re-Bootstrap tragen alle Rows ihr kosmos_id.
    const storedSchemaVersion = await getMetaValue<number>(this.db, 'schemaVersion', 0).catch(() => 0);
    if (storedSchemaVersion < LOCAL_SCHEMA_VERSION && isBrowserOnline()) {
      await setMetaValue(this.db, 'bootstrapDone', false).catch(() => undefined);
    }

    // Läuft bereits ein Bootstrap oder Asset-Resume? Diesen Intervall-Tick nicht
    // doppelt anstoßen — sonst würden poll()/Resume gegen die laufende
    // (minutenlange) Asset-Phase rasen und könnten das Gate verfrüht ausblenden.
    // Stattdessen auf den laufenden Vorgang warten und beenden.
    if (this.bootstrapPromise) { await this.bootstrapPromise; return; }
    if (this.assetResumePromise) { await this.assetResumePromise; return; }

    const bootstrapDone = await getMetaValue(this.db, 'bootstrapDone', false).catch(() => false);
    if (!bootstrapDone) {
      await this.bootstrap();
      const doneAfterBootstrap = await getMetaValue(this.db, 'bootstrapDone', false).catch(() => false);
      if (!doneAfterBootstrap) return;
    } else {
      // Daten sind da, aber die Bilder-Phase wurde beim letzten Lauf
      // unterbrochen (App geschlossen)? Online idempotent fortsetzen — laedt
      // nur Fehlende. Setzt assetsDone=true nach einem vollen Durchlauf, danach
      // wird dieser Zweig nicht mehr betreten.
      let assetsDone = await getMetaValue(this.db, 'assetsDone', false).catch(() => false);
      // Selbst-Heilung (einmalig): ein früherer Prefetch-Lauf kann komplett
      // gescheitert sein (z.B. cross-origin ohne CORS) → assetsDone=true, aber
      // der Blob-Cache ist LEER. Dann einmalig zurücksetzen, damit der Resume
      // die Bilder neu lädt (jetzt mit CORS). `assetHealDone` verhindert eine
      // Endlosschleife, falls die Bilder dauerhaft unerreichbar bleiben.
      // Im Browser gibt es keinen Blob-Cache mehr (prefetchAssetsEnabled=false)
      // — die Heilung waere dort nur unnoetiges Schreiben.
      if (this.prefetchAssetsEnabled && assetsDone && !this.assetsCompleted && isBrowserOnline()) {
        const healDone = await getMetaValue(this.db, 'assetHealDone', false).catch(() => false);
        if (!healDone) {
          const cacheCount = await this.db.assetCache.count().catch(() => 1);
          await setMetaValue(this.db, 'assetHealDone', true).catch(() => undefined);
          if (cacheCount === 0) {
            await setMetaValue(this.db, 'assetsDone', false).catch(() => undefined);
            assetsDone = false;
          }
        }
      }
      // assetsCompleted-Guard: lief die Asset-Phase in dieser Session bereits
      // durch (auch wenn das Meta-Flag nicht persistiert wurde), NICHT erneut
      // anstoßen — sonst flackerte das Gate bei jedem Intervall-Tick.
      if (!assetsDone && !this.assetsCompleted && isBrowserOnline()) {
        await this.resumeAssetsIfPending();
      }
    }

    await this.poll();
  }

  /**
   * W5-E: Den geteilten Vorlagenkatalog EINMAL pro Geraet laden (Plan 5.4).
   *
   * Rueckgabe null heisst „kein Katalogweg": alter Server-Slot ohne
   * /api/catalog, offline, gesperrte IndexedDB. Dann bleibt alles beim Alten —
   * die Katalogzeilen kommen wie bisher ueber die Bestandsendpunkte bzw. die
   * expandierten Katalogseiten im Delta.
   */
  private async ensureCatalogLoaded(): Promise<number | null> {
    try {
      const manifest = await getCatalogManifest();
      const version = Number(manifest?.version ?? 0);
      if (!Number.isFinite(version) || version <= 0) return null;
      const lang = String(manifest.lang ?? 'de');

      const state = await readCatalogState();
      // Zweiter Start desselben Geraets: nichts zu tun. Die Sprache gehoert in
      // den Vergleich, weil die Nutzlast lokalisiert ausgeliefert wird
      // (Plan 5.7) — sonst blieben nach einem Sprachwechsel deutsche Namen.
      if (state && state.version === version && state.lang === lang) return version;

      const payload = await getCatalogPayload(version);
      const loadedVersion = Number(payload?.version) || version;
      await writeCatalog({
        version: loadedVersion,
        lang: String(payload?.lang ?? lang),
        elements: payload?.elements ?? [],
        relationships: payload?.relationships ?? [],
      });
      // Nach einer neuen Nutzlast muss der Server die volle Verweisliste
      // schicken — die alte passt nicht mehr zu den (evtl. neuen) IDs.
      this.catalogFullRequested = true;
      return loadedVersion;
    } catch {
      return null;
    }
  }

  /** IDs, die lokal im Katalog liegen — Grundlage der Bootstrap-Aufteilung. */
  private static async catalogIdSets(): Promise<{ elements: Set<number>; relationships: Set<number> }> {
    const [elements, relationships] = await Promise.all([
      readCatalogElements(),
      readCatalogRelationships(),
    ]);
    return {
      elements: new Set(elements.map((row) => Number(row.id))),
      relationships: new Set(relationships.map((row) => Number(row.id))),
    };
  }

  private async writeBootstrapData(
    data: BootstrapData,
    catalog: { version: number; elementIds: Set<number>; relationshipIds: Set<number> } | null,
  ): Promise<void> {
    // Der Bootstrap-Endpunkt liefert weiterhin die VEREINIGUNG (eigene ∪
    // Katalog, bereits gefiltert und auf die Kosmen des Nutzers projiziert).
    // Mit Katalog-DB behalten wir davon nur die eigenen Zeilen — von den
    // Katalogzeilen bleibt der Verweis (ID + Kosmos). Ohne Katalog-DB bleibt
    // alles wie bisher in worldElements.
    const ownElements: WorldElement[] = [];
    const visible: CatalogVisibleRecord[] = [];
    const ownRelationships: WorldRelationship[] = [];
    if (catalog) {
      for (const row of data.worldElements) {
        const id = Number(row.id);
        // Nur aufteilen, was der lokale Katalog auch wirklich kennt — sonst
        // verschwaende eine Zeile aus der Ansicht (Versionsversatz).
        if (catalog.elementIds.has(id)) {
          visible.push({ id, kosmos_id: (row as { kosmos_id?: number | null }).kosmos_id ?? null });
        } else {
          ownElements.push(row);
        }
      }
      for (const row of data.worldRelationships) {
        if (!catalog.relationshipIds.has(Number(row.id))) ownRelationships.push(row);
      }
    } else {
      ownElements.push(...data.worldElements);
      ownRelationships.push(...data.worldRelationships);
    }

    await this.db.transaction(
      'rw',
      [
        this.db.campaigns,
        this.db.characters,
        this.db.worldElements,
        this.db.worldRelationships,
        this.db.worldTrees,
        this.db.worldTreeNodes,
        this.db.tableRounds,
        this.db.catalogVisible,
        this.db.meta,
      ],
      async () => {
        await this.db.campaigns.clear();
        await this.db.characters.clear();
        await this.db.worldElements.clear();
        await this.db.worldRelationships.clear();
        await this.db.worldTrees.clear();
        await this.db.worldTreeNodes.clear();
        await this.db.tableRounds.clear();
        await this.db.catalogVisible.clear();

        await this.db.campaigns.bulkPut(data.campaigns);
        await this.db.characters.bulkPut(data.characters);
        await this.db.worldElements.bulkPut(ownElements);
        await this.db.worldRelationships.bulkPut(ownRelationships);
        await this.db.worldTrees.bulkPut(data.worldTrees);
        await this.db.worldTreeNodes.bulkPut(data.worldTreeNodes);
        await this.db.tableRounds.bulkPut(data.tableRounds);
        if (catalog) await this.db.catalogVisible.bulkPut(visible);
        await setMetaValue(this.db, 'bootstrapDone', true);
        await setMetaValue(this.db, 'audioDtoVersion', 1);
        await setMetaValue(this.db, 'lastBootstrapAt', nowIso());
        await setMetaValue(this.db, 'syncCursor', data.cursor);
        await setMetaValue(this.db, 'catalogVersion', catalog ? catalog.version : null);
        await markSchemaVersion(this.db);
      },
    );
  }

  /**
   * W5-E: Katalogzeilen gehoeren NIE in die Nutzer-Datenbank.
   *
   * Ein Server mit `?catalog=`-Unterstuetzung nimmt sie bereits aus dem Delta
   * heraus. Ein Server-Slot aus W5-B/W5-C tut das nicht (er kennt den Parameter
   * nicht) — dann traegt das Delta rohe, UNLOKALISIERTE Katalogzeilen. Wuerden
   * wir die schreiben, staenden sie doppelt (Katalog-DB + Nutzer-DB) und der
   * lokal korrekte Name waere ueberschrieben (Plan 5.7). Deshalb dieselbe
   * Trennung auch clientseitig.
   */
  private static isCatalogRow(row: { id: number; catalog_key?: unknown }): boolean {
    return Number(row.id) < 0 || typeof row.catalog_key === 'string';
  }

  private async applyChangePage(page: SyncChangesResponse): Promise<number> {
    const touched = new Set<ServerEntityType>();
    const storedCursor = await getMetaValue<number>(this.db, 'syncCursor', 0);
    if (page.cursor <= storedCursor) return storedCursor;

    const catalogBlock = page.catalog ?? null;
    // version 0 heisst „diese Instanz hat gar keinen Katalog" — dann gibt es
    // nichts nachzuladen, und ein Dauer-Nachladeversuch waere sinnlos.
    if (catalogBlock?.stale && catalogBlock.version > 0) this.catalogReloadPending = true;
    const catalogEnabled = catalogBlock !== null
      || (await getMetaValue<number>(this.db, 'catalogVersion', 0).catch(() => 0)) > 0;

    // Fremdgelieferte Katalogzeilen (alter Server-Slot) heraustrennen.
    let worldElementUpserts = page.changes.world_element.upserts;
    let worldElementDeletions = page.changes.world_element.deletions;
    const strayCatalogUpserts: WorldElement[] = [];
    const strayCatalogDeletions: number[] = [];
    if (catalogEnabled) {
      const own: WorldElement[] = [];
      for (const row of worldElementUpserts) {
        if (SyncEngine.isCatalogRow(row)) strayCatalogUpserts.push(row);
        else own.push(row);
      }
      worldElementUpserts = own;
      const ownDeletions: number[] = [];
      for (const id of worldElementDeletions) {
        if (Number(id) < 0) strayCatalogDeletions.push(Number(id));
        else ownDeletions.push(id);
      }
      worldElementDeletions = ownDeletions;
    }

    await this.db.transaction(
      'rw',
      [
        this.db.campaigns,
        this.db.characters,
        this.db.worldElements,
        this.db.worldRelationships,
        this.db.worldTrees,
        this.db.worldTreeNodes,
        this.db.tableRounds,
        this.db.catalogVisible,
        this.db.meta,
      ],
      async () => {
        if (hasChanges(page.changes.campaign)) touched.add('campaign');
        await this.db.campaigns.bulkPut(page.changes.campaign.upserts);
        await this.db.campaigns.bulkDelete(page.changes.campaign.deletions);

        if (hasChanges(page.changes.character)) touched.add('character');
        await this.db.characters.bulkPut(page.changes.character.upserts);
        await this.db.characters.bulkDelete(page.changes.character.deletions);

        if (hasChanges(page.changes.world_element)) touched.add('world_element');
        await this.db.worldElements.bulkPut(worldElementUpserts);
        await this.db.worldElements.bulkDelete(worldElementDeletions);

        // ── Geteilter Vorlagenkatalog: nur die Verweisliste pflegen ────────
        if (catalogBlock) {
          if (catalogBlock.full) {
            await this.db.catalogVisible.clear();
            await this.db.catalogVisible.bulkPut(
              catalogBlock.visible.map((ref) => ({ id: Number(ref.id), kosmos_id: ref.kosmos_id ?? null })),
            );
            touched.add('world_element');
            touched.add('world_relationship');
          } else {
            if (catalogBlock.visible.length) {
              await this.db.catalogVisible.bulkPut(
                catalogBlock.visible.map((ref) => ({ id: Number(ref.id), kosmos_id: ref.kosmos_id ?? null })),
              );
            }
            if (catalogBlock.hidden.length) {
              await this.db.catalogVisible.bulkDelete(catalogBlock.hidden.map(Number));
            }
            if (catalogBlock.visible.length || catalogBlock.hidden.length) {
              touched.add('world_element');
              touched.add('world_relationship');
            }
          }
        }
        if (strayCatalogDeletions.length) {
          await this.db.catalogVisible.bulkDelete(strayCatalogDeletions);
          await this.db.worldElements.bulkDelete(strayCatalogDeletions);
          touched.add('world_element');
        }
        if (strayCatalogUpserts.length) {
          // Kosmos-Zuordnung eines bereits bekannten Verweises NICHT
          // ueberschreiben — die Rohzeile eines alten Slots traegt keine.
          const ids = strayCatalogUpserts.map((row) => Number(row.id));
          const known = new Set(
            (await this.db.catalogVisible.bulkGet(ids))
              .filter((row): row is CatalogVisibleRecord => !!row)
              .map((row) => row.id),
          );
          const additions = strayCatalogUpserts
            .filter((row) => !known.has(Number(row.id)))
            .map((row) => ({
              id: Number(row.id),
              kosmos_id: (row as { kosmos_id?: number | null }).kosmos_id ?? null,
            }));
          if (additions.length) await this.db.catalogVisible.bulkPut(additions);
          touched.add('world_element');
        }

        if (hasChanges(page.changes.world_relationship)) touched.add('world_relationship');
        await this.db.worldRelationships.bulkPut(page.changes.world_relationship.upserts);
        await this.db.worldRelationships.bulkDelete(page.changes.world_relationship.deletions);

        if (hasChanges(page.changes.world_tree)) touched.add('world_tree');
        await this.db.worldTrees.bulkPut(page.changes.world_tree.upserts);
        await this.db.worldTrees.bulkDelete(page.changes.world_tree.deletions);

        if (hasChanges(page.changes.world_tree_node)) touched.add('world_tree_node');
        await this.db.worldTreeNodes.bulkPut(page.changes.world_tree_node.upserts);
        await this.db.worldTreeNodes.bulkDelete(page.changes.world_tree_node.deletions);

        if (hasChanges(page.changes.table_round)) touched.add('table_round');
        await this.db.tableRounds.bulkPut(page.changes.table_round.upserts);
        await this.db.tableRounds.bulkDelete(page.changes.table_round.deletions);

        // Warum: Cursor erst nach den Writes speichern, damit ein Crash die Seite erneut anfordert.
        const currentCursor = await getMetaValue<number>(this.db, 'syncCursor', 0);
        await setMetaValue(this.db, 'syncCursor', Math.max(currentCursor, page.cursor));
      },
    );

    this.invalidateTouchedQueries(touched);
    return Math.max(storedCursor, page.cursor);
  }

  private invalidateAllSyncQueries(): void {
    this.invalidateTouchedQueries(new Set<ServerEntityType>([
      'campaign',
      'character',
      'world_element',
      'world_relationship',
      'world_tree',
      'world_tree_node',
      'table_round',
    ]));
  }

  private invalidateTouchedQueries(touched: ReadonlySet<ServerEntityType>): void {
    if (touched.has('campaign')) void this.queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all });
    if (touched.has('character')) void this.queryClient.invalidateQueries({ queryKey: queryKeys.characters.all });
    if (touched.has('world_element')) {
      void this.queryClient.invalidateQueries({ queryKey: ['world-elements'] });
      void this.queryClient.invalidateQueries({ queryKey: ['world-trees'] });
      void this.queryClient.invalidateQueries({ queryKey: ['world-tree-nodes'] });
    }
    if (touched.has('world_relationship')) {
      void this.queryClient.invalidateQueries({ queryKey: ['world-relationships'] });
      void this.queryClient.invalidateQueries({ queryKey: ['world-trees'] });
    }
    if (touched.has('world_tree')) void this.queryClient.invalidateQueries({ queryKey: ['world-trees'] });
    if (touched.has('world_tree_node')) void this.queryClient.invalidateQueries({ queryKey: ['world-tree-nodes'] });
    if (touched.has('table_round')) void this.queryClient.invalidateQueries({ queryKey: queryKeys.tableRounds.all });
  }

  private setSnapshot(snapshot: SyncEngineSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener();
  }

  // DATEN-Phase: 0..10 % gewichtet. ETA monoton aus elapsed/step.
  private reportDataProgress(step: number, waiting = false, failed = false): void {
    const safeStep = Math.max(0, Math.min(DATA_STEPS, Math.floor(step)));
    const percentage = Math.max(0, Math.min(10, Math.round((safeStep / DATA_STEPS) * 10)));
    let etaSeconds: number | null = null;
    if (this.bootstrapProgressStartAt !== null && !waiting && safeStep > 0 && safeStep < DATA_STEPS) {
      const elapsedMs = Date.now() - this.bootstrapProgressStartAt;
      if (elapsedMs > 0) {
        const remainingMs = (elapsedMs / safeStep) * (DATA_STEPS - safeStep);
        const rounded = Math.round(remainingMs / 1000);
        etaSeconds = rounded > 0 ? rounded : 0;
      }
    }
    this.setSnapshot({
      status: 'bootstrapping',
      lastSyncAt: this.snapshot.lastSyncAt,
      bootstrapProgress: {
        phase: 'data',
        percentage,
        dataStep: safeStep,
        dataSteps: DATA_STEPS,
        assetsDone: 0,
        assetsTotal: 0,
        bytesDone: 0,
        bytesTotal: 0,
        etaSeconds,
        waitingForNetwork: waiting,
        dataError: failed,
        failedAttempts: this.bootstrapFailures,
      },
    });
  }

  // ASSET-Phase: 10..100 % gewichtet. Der Prozentwert bleibt dateibasiert;
  // bytesTotal ist vor dem ersten Report geplant und waehrend der Downloads
  // eingefroren. Nur der finale Report darf genau einmal korrigieren.
  private reportAssetProgress(done: number, total: number, bytesDone: number, waiting = false): void {
    const safeDone = Math.max(0, Math.min(total, done));
    const percentage = total > 0
      ? Math.max(10, Math.min(100, 10 + Math.round((safeDone / total) * 90)))
      : 100;
    const isFinal = total > 0 && safeDone >= total;
    if (
      isFinal
      && this.assetBytesTotalPlanned !== null
      && !this.assetTotalCorrected
      && this.assetBytesTotalPlanned !== bytesDone
    ) {
      this.assetBytesTotalPlanned = bytesDone;
      this.assetTotalCorrected = true;
    }

    const bytesTotal = this.assetBytesTotalPlanned ?? 0;
    const remainingBytes = Math.max(0, bytesTotal - bytesDone);
    let etaSeconds: number | null = null;
    const measuredBytes = Math.max(0, bytesDone - this.assetBytesAtDownloadStart);
    if (this.assetPhaseStartAt !== null && !waiting && safeDone > 0 && remainingBytes > 0 && measuredBytes > 0) {
      const elapsedMs = Date.now() - this.assetPhaseStartAt;
      const bytesPerMs = elapsedMs > 0 ? measuredBytes / elapsedMs : 0;
      if (bytesPerMs > 0) {
        const rounded = Math.round(remainingBytes / bytesPerMs / 1000);
        etaSeconds = rounded > 0 ? rounded : null;
      }
    }
    this.setSnapshot({
      status: 'bootstrapping',
      lastSyncAt: this.snapshot.lastSyncAt,
      bootstrapProgress: {
        phase: 'assets',
        percentage,
        dataStep: DATA_STEPS,
        dataSteps: DATA_STEPS,
        assetsDone: safeDone,
        assetsTotal: total,
        bytesDone,
        bytesTotal,
        etaSeconds,
        waitingForNetwork: waiting,
      },
    });
  }

  // Registriert vorab gecachte Portrait-Blobs als Object-URLs, damit assetUrl()
  // offline die lokale Kopie liefert. Idempotent, nur einmal pro Engine-Lauf.
  private async rehydrateAssetCache(): Promise<void> {
    if (this.assetCacheRehydrated) return;
    this.assetCacheRehydrated = true;
    try {
      const records = await getAllAssetCacheRecords(this.db);
      for (const record of records) {
        if (!record?.blob || hasCachedAsset(record.path)) continue;
        try {
          registerCachedAsset(record.path, URL.createObjectURL(record.blob));
        } catch {
          /* ignore — Object-URL nicht erzeugbar (Test/SSR) */
        }
      }
    } catch (error) {
      console.warn('[Sync] Asset cache rehydrate failed.', error);
    }
  }

  // Laedt alle Portrait-Bilder offline-faehig in den Dexie-Blob-Cache. Idempotent
  // (ueberspringt bereits gecachte), gedrosselt, robust: ein einzelnes Asset darf
  // den Bootstrap NIE blockieren. Setzt assetsDone=true nach einem vollen Pass.
  private async prefetchAssets(paths: string[]): Promise<void> {
    const total = paths.length;
    this.assetBytesTotalPlanned = null;
    this.assetBytesAtDownloadStart = 0;
    this.assetTotalCorrected = false;
    if (total === 0) {
      this.assetsCompleted = true;
      await setMetaValue(this.db, 'assetsDone', true).catch(() => undefined);
      return;
    }

    let done = 0;
    let bytesDone = 0;

    // Bereits gecachte Pfade in EINEM Lesevorgang ermitteln (statt 321 Counts).
    const cached = new Map<string, AssetCacheRecord>();
    try {
      for (const record of await getAllAssetCacheRecords(this.db)) cached.set(record.path, record);
    } catch {
      /* ignore — leerer Cache */
    }

    const pending: string[] = [];
    for (const path of paths) {
      const record = cached.get(path);
      if (record?.blob) {
        if (!hasCachedAsset(path)) {
          try {
            registerCachedAsset(path, URL.createObjectURL(record.blob));
          } catch {
            /* ignore */
          }
        }
        const cachedSize = typeof record.size === 'number' && Number.isFinite(record.size)
          ? Math.max(0, record.size)
          : 0;
        bytesDone += cachedSize;
        done += 1;
      } else {
        pending.push(path);
      }
    }

    // Groessen genau einmal NACH der Cache-Inventur und VOR dem ersten
    // sichtbaren Asset-Report laden. Bei API-Fehler bleibt der Download robust,
    // aber bytesTotal=0 unterdrueckt fuer diesen Lauf die MB-Zeile.
    let pendingBytes = 0;
    let sizesAvailable = true;
    if (pending.length > 0) {
      try {
        const response = await getAssetSizes(pending);
        if (!response?.sizes || typeof response.sizes !== 'object') throw new Error('asset_sizes_invalid');
        for (const path of pending) {
          const size = response.sizes[path];
          if (size === null) {
            pendingBytes += AVG_ASSET_BYTES;
          } else if (typeof size === 'number' && Number.isFinite(size) && size >= 0) {
            pendingBytes += size;
          } else {
            throw new Error('asset_sizes_invalid');
          }
        }
      } catch (error) {
        sizesAvailable = false;
        console.warn('[Sync] Asset size planning failed; hiding byte progress.', error);
      }
    }

    this.assetBytesTotalPlanned = sizesAvailable ? bytesDone + pendingBytes : null;
    this.assetBytesAtDownloadStart = bytesDone;
    this.assetPhaseStartAt = Date.now();
    this.reportAssetProgress(done, total, bytesDone);

    const fetchOne = async (path: string): Promise<void> => {
      // Kooperativer Abbruch: gestoppte Engine (Logout/User-Wechsel) lädt nicht
      // weiter (verhindert verwaiste Schreibvorgänge in eine schließende DB).
      if (!this.started) return;
      // Offline? warten, bis online ODER gestoppt — NIE abbrechen.
      while (!isBrowserOnline()) {
        if (!this.started) return;
        this.reportAssetProgress(done, total, bytesDone, true);
        await wait(2_000);
      }
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const blob = await fetchBlobWithTimeout(networkAssetUrl(path), ASSET_FETCH_TIMEOUT_MS);
          await putAssetCacheRecord(this.db, { path, blob, size: blob.size, fetchedAt: nowIso() });
          if (!hasCachedAsset(path)) {
            try {
              registerCachedAsset(path, URL.createObjectURL(blob));
            } catch {
              /* ignore */
            }
          }
          bytesDone += blob.size;
          return;
        } catch (error) {
          if (attempt === 1) {
            // Asset-Fehler dürfen den Bootstrap NIE blockieren — überspringen.
            console.warn('[Sync] Asset prefetch failed, skipping.', path, error);
          }
        }
      }
    };

    for (let index = 0; index < pending.length; index += ASSET_CONCURRENCY) {
      // Gestoppt? Pass abbrechen OHNE assetsDone zu setzen — der Rest wird beim
      // nächsten Start idempotent fortgesetzt (nur Fehlende).
      if (!this.started) return;
      const batch = pending.slice(index, index + ASSET_CONCURRENCY);
      await Promise.all(batch.map(async (path) => {
        await fetchOne(path);
        done += 1;
        this.reportAssetProgress(done, total, bytesDone);
      }));
    }

    // Nur nach einem VOLLEN Durchlauf als erledigt markieren. assetsCompleted
    // (In-Memory) ist die robuste Wahrheit; das Meta-Flag persistiert es best-effort.
    this.assetsCompleted = true;
    await setMetaValue(this.db, 'assetsDone', true).catch(() => undefined);
  }

  // Setzt eine beim letzten Lauf unterbrochene Bilder-Phase fort: sammelt die
  // Portrait-Pfade aus dem bereits lokal vorhandenen Bestand und laedt Fehlende.
  private async resumeAssetsIfPending(): Promise<void> {
    if (this.assetResumePromise) return this.assetResumePromise;
    this.assetResumePromise = (async () => {
      try {
        // Im Browser gibt es keine Asset-Phase — es werden also KEINE Pfade
        // gesammelt und nichts geladen. Der Leerfall-Pfad von prefetchAssets
        // muss aber trotzdem laufen: ein aus einer aelteren Version stehen
        // gebliebenes assetsDone=false wuerde poll() sonst dauerhaft auf
        // 'bootstrapping' halten — das Gate klebte fuer immer.
        let paths: string[] = [];
        if (this.prefetchAssetsEnabled) {
          // Phase 2 (Planner): Kampagnen-Cover mit aufnehmen, sonst fehlt das
          // Bild nach einem Resume-Lauf auf der Dashboard-Karte.
          const [worldElements, characters, campaigns] = await Promise.all([
            this.db.worldElements.toArray().catch(() => [] as WorldElement[]),
            this.db.characters.toArray().catch(() => [] as Character[]),
            this.db.campaigns.toArray().catch(() => [] as Campaign[]),
          ]);
          paths = collectAssetPaths({ worldElements, characters, campaigns });
        }
        await this.prefetchAssets(paths);
        this.setSnapshot({ status: 'live', lastSyncAt: nowIso() });
      } catch {
        // Best-effort — die Daten sind ohnehin da; Bilder kommen sonst lazy.
        // assetsCompleted setzen, damit ein Folge-Tick den Resume nicht endlos
        // wiederholt (Review: kein Loop bei wiederholtem Fehlschlag).
        this.assetsCompleted = true;
        await setMetaValue(this.db, 'assetsDone', true).catch(() => undefined);
        this.setSnapshot({ status: 'live', lastSyncAt: this.snapshot.lastSyncAt });
      } finally {
        this.assetResumePromise = null;
        this.assetPhaseStartAt = null;
      }
    })();
    return this.assetResumePromise;
  }

  private scheduleRetry(): void {
    if (!this.started) return;
    this.clearRetry();

    const win = getWindow();
    if (!win) return;

    const delay = this.retryDelayMs;
    this.retryDelayMs = Math.min(this.retryDelayMs * 2, MAX_RETRY_MS);
    this.retryId = win.setTimeout(() => {
      this.retryId = null;
      void this.ensureBootstrappedThenPoll();
    }, delay);
  }

  private clearRetry(): void {
    const win = getWindow();
    if (this.retryId !== null && win) win.clearTimeout(this.retryId);
    this.retryId = null;
  }

  private scheduleIdleTask(task: () => void): void {
    const win = getWindow() as (Window & {
      requestIdleCallback?: (callback: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    }) | null;
    if (!win) {
      task();
      return;
    }

    if (typeof win.requestIdleCallback === 'function') {
      this.idleId = win.requestIdleCallback(task);
      return;
    }

    this.idleId = win.setTimeout(task, 0);
  }

  private clearIdleTask(): void {
    const win = getWindow() as (Window & {
      cancelIdleCallback?: (id: number) => void;
    }) | null;
    if (this.idleId === null || !win) {
      this.idleId = null;
      return;
    }

    if (typeof win.cancelIdleCallback === 'function') win.cancelIdleCallback(this.idleId);
    else win.clearTimeout(this.idleId);
    this.idleId = null;
  }

  private restartInterval(): void {
    this.clearInterval();
    const win = getWindow();
    if (!win || !isDocumentVisible()) return;

    this.intervalId = win.setInterval(() => {
      if (!isDocumentVisible() || !isBrowserOnline()) return;
      if (this.snapshot.status === 'error') return;
      void this.ensureBootstrappedThenPoll();
    }, this.intervalMs);
  }

  private clearInterval(): void {
    const win = getWindow();
    if (this.intervalId !== null && win) win.clearInterval(this.intervalId);
    this.intervalId = null;
  }

  private readonly handleVisibilityChange = (): void => {
    this.restartInterval();
    if (isDocumentVisible()) void this.ensureBootstrappedThenPoll();
  };

  private readonly handleOnline = (): void => {
    void this.ensureBootstrappedThenPoll();
  };

  private readonly handleOffline = (): void => {
    this.setSnapshot({ status: 'offline', lastSyncAt: this.snapshot.lastSyncAt });
  };

  private attachBrowserListeners(): void {
    const win = getWindow();
    if (!win) return;
    win.addEventListener('online', this.handleOnline);
    win.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private detachBrowserListeners(): void {
    const win = getWindow();
    if (!win) return;
    win.removeEventListener('online', this.handleOnline);
    win.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private attachNativeForegroundListener(): void {
    const win = getWindow() as CapacitorWindow | null;
    const addListener = win?.Capacitor?.Plugins?.App?.addListener;
    if (!addListener) return;

    const result = addListener('appStateChange', (state) => {
      if (state.isActive === false) return;
      void this.ensureBootstrappedThenPoll();
    });

    void Promise.resolve(result)
      .then((subscription) => {
        this.removeNativeForegroundListener = () => {
          void subscription.remove?.();
        };
      })
      .catch(() => undefined);
  }
}
